<template>
  <div class="flex flex-col h-full min-h-0">
    <div class="cm-toolbar shrink-0">
      <h2>Team Review</h2>
      <div v-if="boards.length > 1" class="cm-seg ml-4">
        <button
          v-for="b in boards"
          :key="b.id"
          :class="{ on: b.id === boardId }"
          @click="selectBoard(b.id)"
        >{{ b.name }}</button>
      </div>
      <span v-else-if="board" class="text-sm text-gray-500 ml-3">{{ board.name }}</span>
      <div v-if="board" class="flex items-center gap-2 ml-4">
        <div class="cm-stat is-brand">
          <span class="cm-stat-label">Surfaced</span>
          <span class="cm-stat-value">{{ board.surfaced }}</span>
        </div>
        <div class="cm-stat">
          <span class="cm-stat-label">Assessed</span>
          <span class="cm-stat-value">{{ board.total }}</span>
        </div>
        <span v-if="board.last_result_at" class="text-xs text-gray-400">latest {{ shortDate(board.last_result_at) }}</span>
        <span v-if="board.running" class="cm-badge info">Assessment running</span>
      </div>
      <div class="flex-1"></div>
      <button
        v-if="board && category === null"
        class="cm-btn cm-btn-sm"
        :class="showAll ? 'cm-btn-primary' : 'cm-btn-secondary'"
        @click="toggleAll"
      >Show not surfaced</button>
      <button class="cm-btn cm-btn-sm cm-btn-secondary" @click="loadBoards">Refresh</button>
    </div>

    <div v-if="loadingBoards" class="cm-loading flex-1">
      <div class="cm-spinner"></div>
    </div>

    <div v-else-if="!boards.length" class="cm-empty flex-1">
      <div class="ico"><font-awesome-icon :icon="['fas', 'hospital']" /></div>
      <div class="msg">No team review template is configured for this tenant.</div>
    </div>

    <div v-else class="flex-1 flex min-h-0">
      <div class="w-80 shrink-0 border-r border-gray-200 flex flex-col min-h-0">
        <div class="px-3 py-2 border-b border-gray-100 shrink-0 space-y-2">
          <select
            v-if="board?.categories?.length"
            class="cm-select cm-input-sm w-full"
            :value="category || ''"
            @change="selectCategory($event.target.value || null)"
          >
            <option value="">All reasons ({{ board.surfaced }})</option>
            <option
              v-for="c in board.categories"
              :key="c.key"
              :value="c.key"
              :disabled="!c.count"
            >{{ c.label }} ({{ c.count }})</option>
          </select>
          <input v-model="search" data-demo-search class="sensitive cm-input cm-input-sm w-full" placeholder="Search patient or reason..." />
        </div>
        <div class="flex-1 overflow-y-auto min-h-0">
          <div v-if="loadingPatients" class="p-3 space-y-2">
            <div v-for="n in 10" :key="n" class="h-14 bg-gray-100 rounded animate-pulse"></div>
          </div>
          <template v-else>
            <button
              v-for="(p, i) in filteredPatients"
              :key="p.result_id"
              class="w-full text-left px-3 py-2.5 border-b border-gray-100 transition-colors"
              :class="[selectedId === p.result_id ? 'bg-primary-50' : 'hover:bg-gray-50', p.surface ? '' : 'opacity-60']"
              @click="selectPatient(p)"
            >
              <div class="flex items-center gap-2">
                <span class="w-5 text-[11px] font-semibold text-gray-400 text-right shrink-0">{{ i + 1 }}</span>
                <span class="sensitive text-[13px] font-medium text-gray-800 truncate">{{ titleCase(p.patient_name) || p.patient_identifier }}</span>
                <span v-if="p.rank != null" class="ml-auto text-xs font-semibold shrink-0" :class="rankColor(p.rank)">{{ Math.round(p.rank) }}</span>
              </div>
              <div v-if="p.categories.length" class="flex flex-wrap items-center gap-1 mt-1 pl-7">
                <span
                  v-for="c in orderedCategories(p)"
                  :key="c.category"
                  class="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
                  :class="category && c.category !== category ? 'opacity-50' : ''"
                  :style="pill(categoryColor(c.category))"
                >{{ categoryLabel(c.category) }}</span>
              </div>
              <div class="sensitive text-[11px] text-gray-500 mt-1 pl-7 line-clamp-2" data-demo-blur>{{ p.title }}</div>
            </button>
            <div v-if="!filteredPatients.length" class="cm-empty">
              <div class="msg">{{ patients.length ? 'No patients match.' : (category ? 'No patients on this list.' : 'No patients surfaced in the latest assessment.') }}</div>
            </div>
          </template>
        </div>
      </div>

      <div class="flex-1 min-w-0 flex flex-col min-h-0 bg-white">
        <div v-if="!selectedId" class="cm-empty flex-1">
          <div class="msg">Select a patient to see why they were surfaced.</div>
        </div>
        <template v-else>
          <div class="cm-toolbar shrink-0">
            <h2 class="sensitive truncate">{{ titleCase(detailName) || detail?.patient_identifier }}</h2>
            <span v-if="detail" class="sensitive cm-mono text-xs text-gray-400 ml-2">{{ detail.patient_identifier }}</span>
            <span v-if="detail" class="text-xs text-gray-400 ml-3">assessed {{ shortDate(detail.assessed_at) }}</span>
            <div class="flex-1"></div>
            <button
              v-if="detail"
              class="cm-btn cm-btn-sm cm-btn-secondary"
              @click="drawerOpen = true"
            >Chart</button>
          </div>
          <div class="flex-1 overflow-y-auto min-h-0 p-5 bg-gray-50">
            <div v-if="loadingDetail" class="space-y-3">
              <div v-for="n in 6" :key="n" class="h-6 bg-gray-100 rounded animate-pulse"></div>
            </div>
            <div v-else-if="detail && sections.length" :key="detail.result_id" class="idt-detail space-y-3">
              <section
                v-for="(sec, si) in visibleSections"
                :key="si"
                class="cm-panel shadow-sm"
              >
                <div v-if="sec.title" class="cm-panel-head">
                  <div class="cm-panel-title">{{ sec.title }}</div>
                </div>
                <div class="px-4 py-3">
                  <ReportBlocks
                    :blocks="sec.blocks"
                    :output="detail.data"
                    :entity-id="detail.entity_id || 0"
                  />
                </div>
              </section>
            </div>
            <div v-else-if="detail && blocks.length" class="idt-detail">
              <ReportBlocks
                :key="detail.result_id"
                :blocks="blocks"
                :output="detail.data"
                :entity-id="detail.entity_id || 0"
              />
            </div>
          </div>
        </template>
      </div>
    </div>

    <div
      v-if="drawerOpen && detail"
      class="fixed inset-0 z-40 bg-black/20"
      @click.self="drawerOpen = false"
    >
      <div class="absolute right-0 top-0 h-full w-[50%] min-w-[520px] bg-white shadow-xl overflow-auto">
        <Patient_Drawer
          :patient-identifier="detail.patient_identifier"
          :entity-id="detail.entity_id"
          :patient-name="detailName"
          :enabled-tabs="drawerTabs"
          :show-header="true"
          default-tab="details"
          metric-category="Care Management"
          @close="drawerOpen = false"
        />
      </div>
    </div>
  </div>
</template>

<script>
import '@/assets/cm-tokens.css';
import Patient_Drawer from '@/components/Patient_Drawer/Patient_Drawer.vue';
import ReportBlocks from '@/components/PersonalAnalyst/ReportBlocks.vue';
import demoProtectedFields from '@/mixins/demoProtectedFields';

const TINTS = {
  green: ['#E3F3E0', '#282C27'],
  yellow: ['#FDF1DC', '#7A5214'],
  red: ['#FDECEC', '#D53D3D'],
  gray: ['#F1F1F1', '#626264'],
  navy: ['#EBF4FF', '#21426C'],
  orange: ['#FEE3D9', '#723B28'],
};

export default {
  name: 'CareManagementUM',
  components: { Patient_Drawer, ReportBlocks },
  mixins: [demoProtectedFields],
  // The patient/reason search identifies real patients. Demo Mode empties it and the tagged
  // input is read-only while protected. It is not persisted.
  demoProtectedFields: ['search'],
  data() {
    return {
      loadingBoards: true,
      loadingPatients: false,
      loadingDetail: false,
      boards: [],
      boardId: null,
      category: null,
      patients: [],
      showAll: false,
      search: '',
      selectedId: null,
      detail: null,
      drawerOpen: false,
      drawerTabs: ['details', 'utilization', 'programs', 'engagement', 'care_graph', 'care_team'],
    };
  },
  computed: {
    board() {
      return this.boards.find(b => b.id === this.boardId) || null;
    },
    categoryMeta() {
      return Object.fromEntries((this.board?.categories || []).map(c => [c.key, c]));
    },
    blocks() {
      return this.board?.display_schema?.blocks || [];
    },
    sections() {
      return this.board?.display_schema?.sections || [];
    },
    visibleSections() {
      return this.sections.filter(sec => !sec.requires || this.hasValue(sec.requires));
    },
    filteredPatients() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.patients;
      return this.patients.filter(p =>
        (p.patient_name || '').toLowerCase().includes(q)
        || (p.patient_identifier || '').toLowerCase().includes(q)
        || (p.title || '').toLowerCase().includes(q)
        || p.categories.some(c => this.categoryLabel(c.category).toLowerCase().includes(q) || (c.why || '').toLowerCase().includes(q))
      );
    },
    detailName() {
      return this.detail?.patient_name || this.patients.find(p => p.result_id === this.selectedId)?.patient_name || '';
    },
  },
  mounted() {
    this.loadBoards();
  },
  methods: {
    async loadBoards() {
      this.loadingBoards = true;
      try {
        const { data } = await this.$axios.get('/api/care-management/idt/boards/');
        this.boards = data?.boards || [];
        if (!this.boards.some(b => b.id === this.boardId)) {
          this.boardId = this.boards[0]?.id || null;
        }
      } catch (e) {
        console.error('Error loading team review boards:', e);
        this.boards = [];
      } finally {
        this.loadingBoards = false;
      }
      if (this.boardId) await this.loadPatients();
    },
    async loadPatients() {
      this.loadingPatients = true;
      try {
        const { data } = await this.$axios.get(
          `/api/care-management/idt/boards/${this.boardId}/patients/`,
          { params: { ...(this.category ? { category: this.category } : {}), ...(this.showAll && !this.category ? { all: '1' } : {}) } },
        );
        this.patients = data?.patients || [];
      } catch (e) {
        console.error('Error loading team review patients:', e);
        this.patients = [];
      } finally {
        this.loadingPatients = false;
      }
      if (this.selectedId && !this.patients.some(p => p.result_id === this.selectedId)) {
        this.selectedId = null;
        this.detail = null;
      }
    },
    async selectBoard(id) {
      if (id === this.boardId) return;
      this.boardId = id;
      this.category = null;
      this.selectedId = null;
      this.detail = null;
      await this.loadPatients();
    },
    async selectCategory(key) {
      if (key === this.category) return;
      this.category = key;
      await this.loadPatients();
    },
    async toggleAll() {
      this.showAll = !this.showAll;
      await this.loadPatients();
    },
    async selectPatient(p) {
      this.selectedId = p.result_id;
      this.detail = null;
      this.loadingDetail = true;
      try {
        const { data } = await this.$axios.get(`/api/care-management/idt/results/${p.result_id}/`);
        if (this.selectedId === p.result_id) this.detail = data;
      } catch (e) {
        console.error('Error loading team review detail:', e);
      } finally {
        if (this.selectedId === p.result_id) this.loadingDetail = false;
      }
    },
    hasValue(path) {
      let node = this.detail?.data;
      for (const part of String(path).split('.')) {
        if (node === null || node === undefined) return false;
        node = node[part];
      }
      if (Array.isArray(node)) return node.length > 0;
      return node !== null && node !== undefined && node !== '';
    },
    categoryLabel(key) {
      return this.categoryMeta[key]?.label || (key || '').replace(/_/g, ' ');
    },
    categoryColor(key) {
      return this.categoryMeta[key]?.color || 'gray';
    },
    orderedCategories(p) {
      if (!this.category) return p.categories;
      return [...p.categories].sort((a, b) => (b.category === this.category) - (a.category === this.category));
    },
    pill(color) {
      const [bg, fg] = TINTS[color] || TINTS.gray;
      return { background: bg, color: fg };
    },
    rankColor(v) {
      if (v >= 80) return 'text-red-600';
      if (v >= 55) return 'text-amber-600';
      return 'text-gray-500';
    },
    titleCase(s) {
      return (s || '').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
    },
    shortDate(v) {
      if (!v) return '—';
      const m = String(v).match(/(\d{4})-(\d{2})-(\d{2})/);
      if (!m) return String(v);
      return `${Number(m[2])}/${Number(m[3])}/${m[1].slice(2)}`;
    },
  },
};
</script>

<style scoped>
.idt-detail :deep(.text-sm) { font-size: 12.5px; line-height: 1.5; }
.idt-detail :deep(.text-\[15px\]) { font-size: 13.5px; line-height: 1.5; }
.idt-detail :deep(.text-\[13px\]) { font-size: 12px; }
.idt-detail :deep(.text-\[12px\]) { font-size: 11.5px; }
.idt-detail :deep(.heading-subsection) { font-size: 12px; margin-bottom: 4px; }
.idt-detail :deep(.gap-4) { gap: 12px; }
.idt-detail :deep(.space-y-3 > * + *) { margin-top: 8px; }
.idt-detail :deep(ul.space-y-1 > * + *) { margin-top: 2px; }
</style>
